import "./BarChart.css";

import { ChartData, ChartOptions } from "chart.js";
import { Bar } from "react-chartjs-2";

import { ColorConstants } from "../../../constants";
import { useChartVariant } from "../ChartVariantContext";
import { ChartProps } from "../models";
import { ChartVariant, getChartTheme, maybeSetGlobalChartJsConfiguration, themeFont } from "../utils";

export interface BarChartProps extends ChartProps {
  suffix?: string;
  /**
   * Design-system brand: `"hub"` (Space Grotesk, square) or `"seed"` (Figtree, rounded).
   * Defaults to the nearest ChartVariantProvider (Hub if none).
   */
  variant?: ChartVariant;
}

maybeSetGlobalChartJsConfiguration();

export const BarChart = ({
  data,
  emptyPlaceholder = "No data to display",
  suffix,
  variant,
}: BarChartProps): JSX.Element => {
  const contextVariant = useChartVariant();
  const theme = getChartTheme(variant ?? contextVariant);

  if (!data || Object.keys(data).length === 0)
    return (
      <div className="Bar Bar_empty">
        <p className="body3">{emptyPlaceholder}</p>
      </div>
    );

  const options: ChartOptions<"bar"> = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        displayColors: false,
        cornerRadius: theme.tooltipCornerRadius,
        titleFont: themeFont(theme),
        bodyFont: themeFont(theme),
        callbacks: {
          title: () => "",
        },
      },
    },
    scales: {
      x: {
        ticks: {
          padding: 6,
          font: themeFont(theme),
        },
        grid: {
          display: false,
          drawTicks: false,
        },
        border: {
          color: ColorConstants.GREY_100,
        },
      },
      y: {
        grid: {
          color: ColorConstants.INDIGO_25,
          tickColor: ColorConstants.WHITE,
        },
        border: {
          display: false,
        },
        ticks: {
          font: themeFont(theme),
          callback: (value) => {
            return suffix ? `${value} ${suffix}` : value;
          },
        },
      },
    },
  };

  const xLabels = Object.keys(data);
  const dataPoints = xLabels.map((label) => data[label]);
  const chartData: ChartData<"bar", number[], string> = {
    labels: xLabels,
    datasets: [
      {
        data: dataPoints,
        barPercentage: 0.62,
        categoryPercentage: 0.7,
        maxBarThickness: 56,
        // Seed rounds the bar tops; Hub keeps them square. (The baseline edge is skipped by
        // default, so any radius only affects the top corners.)
        borderRadius: theme.barBorderRadius,
        hoverBackgroundColor: ColorConstants.INDIGO_400,
      },
    ],
  };
  return (
    <div className="Bar">
      <Bar data={chartData} options={options} />
    </div>
  );
};
